/**
 * 「语音录入」按小题注入（knowledgeType 28 / 33）。
 *
 * 需求：学生作答期间，若该小题最外层字段 knowledgeType 为 28 或 33，
 * 则在该小题的答案空位（正文/答题卡里 [name=tagId] 的 input / textarea）右侧
 * 注入一个录音图标；点击图标弹出「语音录入」弹窗，录音结束后走
 * 上传 -> ASR -> 轮询结果 的链路做语音转文字，识别结果可手动修改 / 删除。
 *
 * 口径与后台 english-user-management 的 components/StudentTask/index.vue 保持一致
 * （那边的 getVoiceTagMap / renderVoiceInputButtons / 弹窗交互是同源实现）：
 *   - 小题级门：String(flow.knowledgeType) === "28" || "33"，挂在**小题(flow)**自己身上；
 *   - 图标类名沿用 .voice-input-mic，便于两端检索与样式对照；
 *   - 图标只作为空位的**兄弟节点**插入，不替换空位本身。
 *
 * ⚠️ 为什么只插入兄弟节点很重要：
 * PenAndComp.getData() 会把 textInputList / textareaInputList 缓存成静态 NodeList，
 * getFormDataFromDOM()、onPhoto() 只认这两个列表。只要不动空位元素本身，
 * 录音图标插在左侧还是右侧都不影响答案的采集与回填（对比 replaceWritingInputWithTextarea
 * 那种「换掉元素」的写法则必须抢在取列表之前执行）。
 *
 * ⚠️ 容器去重（本项目特有的坑）：
 * H5 的答题卡（.answer-sheet-scroll）是**嵌在** #task-detail 里的，
 * 不像后台是两个平级容器。因此「按容器逐个注入」会把同一批空位处理两遍，
 * 所以这里用 nextElementSibling 判断图标是否已存在，保证幂等。
 *
 * 样式由各页面自行声明（注入节点不带 scoped 的 data-v 属性）。
 */

/**清理上一轮注入节点用的选择器（重复渲染时避免叠加）**/
const MIC_SELECTOR = ".voice-input-mic";

/**录音图标（内联 SVG，避免依赖 icon 字体在注入节点上是否可用）**/
const MIC_SVG =
  '<svg viewBox="0 0 1024 1024" aria-hidden="true">' +
  '<path d="M512 640a128 128 0 0 0 128-128V256a128 128 0 1 0-256 0v256a128 128 0 0 0 128 128z"/>' +
  '<path d="M768 448a32 32 0 0 0-32 32v32a224 224 0 1 1-448 0v-32a32 32 0 1 0-64 0v32c0 146.4 109.6 267.5 251.2 285.8V896H384a32 32 0 0 0 0 64h256a32 32 0 0 0 0-64H544v-98.2C685.6 779.5 800 658.4 800 512v-32a32 32 0 0 0-32-32z"/>' +
  "</svg>";

/**把字段值归一化成去空白的字符串，兼容后端返回数字 28 或字符串 "28" 两种写法**/
function normalizeField(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

/**
 * 当前小题是否需要「语音录入」——小题级门。
 * knowledgeType 挂在**小题(flow)**自己身上：28 / 33 为需要语音录入的题型。
 */
export function isVoiceInputKnowledgeType(value) {
  const v = normalizeField(value);
  return v === "28" || v === "33";
}

/**
 * 从 flows / taskFlowList 里挑出需要语音录入的小题，返回 tagId → true 的集合。
 */
export function getVoiceInputTagMap(flowList) {
  const map = {};
  const list = Array.isArray(flowList) ? flowList : [];
  list.forEach(flow => {
    if (!flow || !flow.tagId) return;
    if (isVoiceInputKnowledgeType(flow.knowledgeType)) map[flow.tagId] = true;
  });
  return map;
}

/**把容器入参（DOM 元素 id 或元素本身）统一解析成有效元素数组**/
function resolveContainers(containers) {
  const nodes = Array.isArray(containers) ? containers : [];
  return nodes
    .map(item =>
      typeof item === "string" ? document.getElementById(item) : item,
    )
    .filter(node => node && node.nodeType === 1);
}

/**生成录音图标（点击回调由调用方传入，避免 util 依赖具体页面）**/
function createMicButton(handler) {
  const btn = document.createElement("span");
  btn.className = "voice-input-mic";
  btn.title = "语音录入";
  btn.innerHTML = MIC_SVG;
  btn.addEventListener("click", e => {
    // 空位所在容器上绑了 click -> getFormDataFromDOM()，这里要阻止冒泡避免多余同步
    e.preventDefault();
    e.stopPropagation();
    if (typeof handler === "function") handler(btn);
  });
  return btn;
}

/**
 * 给 knowledgeType 28/33 小题的答案空位右侧注入录音图标。
 * @param {Array} flowList 试卷的 flows / taskFlowList
 * @param {Array} containers 容器（DOM 元素 id 或元素本身），按先后顺序尝试
 * @param {Function} onOpen 点击图标时的回调，入参为（该图标、命中的空位元素）
 * @returns {number} 实际注入的图标个数
 */
export function renderVoiceInputButtons(flowList, containers, onOpen) {
  const tagMap = getVoiceInputTagMap(flowList);
  if (!Object.keys(tagMap).length) return 0;
  const list = resolveContainers(containers);
  if (!list.length) return 0;

  // 重复渲染（重新 init / 重新进入）时先清掉上一轮注入的图标
  list.forEach(container => {
    container
      .querySelectorAll(MIC_SELECTOR)
      .forEach(node => node.parentNode && node.parentNode.removeChild(node));
  });

  let count = 0;
  list.forEach(container => {
    // 空位在试卷 HTML 里可能是单行 input，也可能是 textarea，两种都要能挂上图标
    // （name 均为该小题 tagId）
    const blanks = [
      ...container.querySelectorAll('input[type="text"]'),
      ...container.querySelectorAll("textarea"),
    ];
    blanks.forEach(input => {
      if (!input || !input.name || !tagMap[input.name]) return;
      if (!input.parentNode) return;
      // 幂等：容器嵌套（答题卡在 #task-detail 内）时避免同一空位插两次
      const next = input.nextElementSibling;
      if (next && next.classList && next.classList.contains("voice-input-mic")) {
        return;
      }
      input.parentNode.insertBefore(
        createMicButton(() => {
          if (typeof onOpen === "function") onOpen(input);
        }),
        input.nextSibling,
      );
      count++;
    });
  });
  return count;
}

export default {
  isVoiceInputKnowledgeType,
  getVoiceInputTagMap,
  renderVoiceInputButtons,
};
